import type { ReactNode } from "react";

/**
 * Small line icons for the homepage sport labels ("Best for MTB" pills and
 * the More sports menu). The repo's only sport glyphs are the emoji in
 * lib/conditions/sport-meta.ts, which do not suit a line-drawn UI, so these
 * are drawn here: 24px grid, currentColor stroke, rounded joins. Always
 * decorative (aria-hidden): the text beside each icon names the sport.
 * No hooks, so server and client components can both use it.
 */

export type HomeIconSport =
  | "mtb"
  | "surf"
  | "wing-foil"
  | "wind"
  | "piste"
  | "ski"
  | "bodyboarding"
  | "sup"
  | "sailing"
  | "diving"
  | "open-water";

/** Crossed skis with turned-up tips (the card key is "piste", the chip key "ski"). */
const CROSSED_SKIS: ReactNode = (
    <>
      <path d="M4.5 4.5C5.2 3.4 6.4 3.3 7.2 4.2L19.5 20.5" />
      <path d="M19.5 4.5C18.8 3.4 17.6 3.3 16.8 4.2L4.5 20.5" />
    </>
);

const PATHS: Record<HomeIconSport, ReactNode> = {
  // Bicycle: two wheels, diamond frame, saddle and bars.
  mtb: (
    <>
      <circle cx="5.5" cy="16.5" r="3.5" />
      <circle cx="18.5" cy="16.5" r="3.5" />
      <path d="M5.5 16.5L9 9.5H15.5M9 9.5L12.5 16.5L15.5 9.5L18.5 16.5M9 9.5L8.4 7H11M15.5 9.5L14.8 6.5H17" />
    </>
  ),
  // Surfboard on the diagonal with its stringer.
  surf: (
    <>
      <path d="M19.5 4.5C13 5 5.5 11.5 4.5 19.5C12.5 18.5 19 11 19.5 4.5Z" />
      <path d="M7 17L17 7" />
    </>
  ),
  // Wing: arched leading edge, centre strut and boom, over a line of water.
  "wing-foil": (
    <>
      <path d="M3.5 14C5 8 11 4 20.5 3.5L14 14.5Z" />
      <path d="M9.5 7.2L14 14.5" />
      <path d="M3 19.5C4.5 19.5 4.5 18.5 6 18.5S7.5 19.5 9 19.5 10.5 18.5 12 18.5 13.5 19.5 15 19.5 16.5 18.5 18 18.5 19.5 19.5 21 19.5" />
    </>
  ),
  // Kite: leading-edge arc, lines down to the bar.
  wind: (
    <>
      <path d="M3 9.5C7 4.5 17 4.5 21 9.5" />
      <path d="M3 9.5L12 19.5L21 9.5" />
      <path d="M9 19.5H15" />
    </>
  ),
  piste: CROSSED_SKIS,
  ski: CROSSED_SKIS,
  // Bodyboard: wide nose tapering to a crescent tail.
  bodyboarding: (
    <>
      <path d="M6 6.5C6 4.5 8.7 3 12 3S18 4.5 18 6.5L16.6 19.2C16.5 20.1 15.6 20.6 14.8 20.3L12 19.2L9.2 20.3C8.4 20.6 7.5 20.1 7.4 19.2Z" />
      <path d="M8.5 8.5C10.5 7.6 13.5 7.6 15.5 8.5" />
    </>
  ),
  // Paddle board with paddle.
  sup: (
    <>
      <path d="M3 17.5C3 16.4 7 15.5 12 15.5S21 16.4 21 17.5 17 19.5 12 19.5 3 18.6 3 17.5Z" />
      <path d="M16.5 3L13 12.5" />
      <path d="M13 12.5L11.8 14.3M13 12.5L13.9 14.6" />
    </>
  ),
  // Sailing boat: mast, mainsail, jib, hull.
  sailing: (
    <>
      <path d="M12 3V17" />
      <path d="M12 4.5L18.5 15H12" />
      <path d="M10 7L5.5 15H10" />
      <path d="M3.5 17.5H20.5L18.5 20.5H5.5Z" />
    </>
  ),
  // Dive mask with snorkel.
  diving: (
    <>
      <path d="M3 8.5H17V13C17 14.1 16.1 15 15 15H12.5L10 13L7.5 15H5C3.9 15 3 14.1 3 13Z" />
      <path d="M20 3.5V15C20 17.5 18.5 19 16 19H13" />
    </>
  ),
  // Swimmer over two lines of water.
  "open-water": (
    <>
      <circle cx="16.5" cy="6" r="2" />
      <path d="M4 12L9 8.5L12.5 11.5L17 10.5" />
      <path d="M3 16C4.5 16 4.5 15 6 15S7.5 16 9 16 10.5 15 12 15 13.5 16 15 16 16.5 15 18 15 19.5 16 21 16" />
      <path d="M3 20C4.5 20 4.5 19 6 19S7.5 20 9 20 10.5 19 12 19 13.5 20 15 20 16.5 19 18 19 19.5 20 21 20" />
    </>
  ),
};

export function isHomeIconSport(sport: string): sport is HomeIconSport {
  return Object.hasOwn(PATHS, sport);
}

/** A decorative line icon for a homepage sport, or nothing for an unknown key. */
export function HomeSportIcon({ sport, size = 15 }: { sport: string; size?: number }) {
  if (!isHomeIconSport(sport)) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-sport-icon={sport}
      className="shrink-0"
    >
      {PATHS[sport]}
    </svg>
  );
}
